import {
  createContext,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
} from 'react';
import type { IsoDate } from '../../domain/dates';
import { LanguageProvider } from '../i18n/LanguageProvider';
import { languageFromLocale, type AppData } from './app-data';
import { requestPersistentStorage, todayInBerlin } from './clock';
import { createPlanRunner, type PlanCalculation } from './plan-runner';
import type { EditResult } from './plan-service';
import { reducer, type Action } from './reducer';
import { loadAppData, saveAppData } from './storage';

interface AppDataState {
  data: AppData;
  dispatch: Dispatch<Action>;
  today: IsoDate;
  /**
   * DE: Plan rechnen und speichern, ohne inzwischen gemachte Änderungen zu überschreiben.
   * EN: Calculate and store a plan without overwriting changes made meanwhile.
   */
  runPlan: (calculate: PlanCalculation) => Promise<EditResult>;
}

const AppDataContext = createContext<AppDataState | undefined>(undefined);

// DE: Ein neuer Plan oder eine Sicherung ändert alle drei Reiter; React zeichnet sie dann in
// kleinen Stücken, damit der Bildschirm nicht hängt. Häkchen bleiben sofort sichtbar.
// EN: A new plan or a backup changes all three tabs; React then draws them in small pieces so the
// screen does not freeze. Ticks stay immediate.
const GENTLE_ACTIONS: ReadonlySet<Action['type']> = new Set(['store-plan', 'restore-backup']);

// DE: Die neuesten Daten stehen sofort bereit, damit eine Plan-Rechnung Änderungen bemerkt, bevor
// React neu zeichnet.
// EN: The newest data is ready at once, so a plan calculation notices changes before React redraws.
function useAppStore(load: () => AppData) {
  const [data, setData] = useState(load);
  const latest = useRef(data);
  const dispatch = useCallback((action: Action) => {
    const next = reducer(latest.current, action);
    latest.current = next;
    if (GENTLE_ACTIONS.has(action.type)) startTransition(() => setData(next));
    else setData(next);
  }, []);
  const runPlan = useCallback(
    (calculate: PlanCalculation) =>
      createPlanRunner({ read: () => latest.current, apply: dispatch })(calculate),
    [dispatch],
  );
  return { data, dispatch, runPlan };
}

// DE: Einmal pro Minute reicht, damit nach Mitternacht der neue Tag erscheint.
// EN: Once a minute is enough for the new day to appear after midnight.
const TODAY_CHECK_MS = 60_000;

// DE: Das heutige Datum bleibt aktuell, auch wenn die App über Nacht offen war oder zurückkehrt.
// EN: Today's date stays current even when the app stayed open overnight or comes back.
function useToday(now: () => Date): IsoDate {
  const [today, setToday] = useState(() => todayInBerlin(now()));
  useEffect(() => {
    const refresh = () => setToday(todayInBerlin(now()));
    const timer = setInterval(refresh, TODAY_CHECK_MS);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [now]);
  return today;
}

/**
 * DE: Lädt die Daten vom Gerät, speichert jede Änderung und stellt Sprache und heutiges Datum bereit.
 * EN: Loads the data from the device, saves every change and provides language and today's date.
 */
export function AppDataProvider({
  storage,
  now,
  locale,
  children,
}: {
  storage: Storage;
  now: () => Date;
  locale: string;
  children: ReactNode;
}) {
  const { data, dispatch, runPlan } = useAppStore(() =>
    loadAppData(storage, languageFromLocale(locale)),
  );
  useEffect(() => saveAppData(storage, data), [storage, data]);
  useEffect(() => {
    void requestPersistentStorage(globalThis.navigator?.storage);
  }, []);
  const value = { data, dispatch, today: useToday(now), runPlan };
  return (
    <AppDataContext.Provider value={value}>
      <LanguageProvider language={data.language}>{children}</LanguageProvider>
    </AppDataContext.Provider>
  );
}

/**
 * DE: Gespeicherte Daten, Änderungsfunktion und heutiges Datum.
 * EN: Stored data, change function and today's date.
 */
export function useAppData(): AppDataState {
  const state = useContext(AppDataContext);
  if (!state) throw new Error('useAppData needs an AppDataProvider');
  return state;
}
